import { parseJSON, subMonths } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiClientFetchSynonymSearchApiKey';
import { apiCreateTmpStorage } from '#/api/tmpStorage/apiCreateTmpStorage';
import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { getCompensationSurveyFields } from '#/components/Form/Fields/CompensationSurveyFields';
import { Form } from '#/components/Form/Form';
import type {
  FormSubmitFunction,
  FormSubmitFunctionData,
} from '#/components/Form/FormContext';
import { MultiStepForm } from '#/components/Form/MultiStepForm/MultiStepForm';
import type { MultiStepFormValues } from '#/components/Form/MultiStepForm/MultiStepFormTypes';
import { useMultiStep } from '#/components/Form/MultiStepForm/useMultiStep';
import { Icon } from '#/components/Icon/Icon';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { LoadingDots } from '#/components/LoadingDots/LoadingDots';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { SuccessCheckmark } from '#/components/SuccessCheckmark/SuccessCheckmark';
import { Body } from '#/components/Text/Body';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { BodySmall } from '#/components/Text/BodySmall';
import { H4 } from '#/components/Text/H4';
import { Text } from '#/components/Text/Text';
import { Underline } from '#/components/Text/Underline';
import { useAppConfig } from '#/config/AppConfigContext';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { salaryApiGetCompensationSurvey } from '#/modules/salary/api/salaryApiGetCompensationSurvey';
import { salaryApiSubmitCompensationSurvey } from '#/modules/salary/api/salaryApiSubmitCompensationSurvey';
import { salaryApiValidateCompensationSurvey } from '#/modules/salary/api/salaryApiValidateCompensationSurvey';
import type { SalaryApiTmpStorage } from '#/modules/salary/api/tmpStorage/types/SalaryApiTmpStorage';
import {
  salariesExploreRoute,
  salariesFaqRoute,
  salariesLearnMoreRoute,
} from '#/modules/salary/routing/salary.routes';
import type { SalariesCompensationSurveyData } from '#/modules/salary/types/SalariesCompensationSurveyData';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { now } from '#/utils/date';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { getItem, setItem } from '#/utils/localStorage';
import { getParam } from '#/utils/url/getParam';
import { salarySurveyLastSubmittedDate } from '#/utils/userEnvironment';
import { navigationFlash } from '#/zustand-stores/flashesStore';

function Footer({
  loggedOut,
  isStepZero,
}: {
  loggedOut?: boolean;
  isStepZero?: boolean;
}) {
  const { i18n } = useI18n();

  return !loggedOut ? (
    <>
      {isStepZero ? (
        <Text styleAs="bodySmall">
          {
            /* TRANSLATORS: Salary survey footer */
            i18n(
              `Want to learn more about Idealist's Nonprofit Salary Explorer? {{link}}Click here to learn more.{{/link}}`,
              {
                link: (
                  <StyledNativeLinkWithRef
                    target="_blank"
                    href={salariesFaqRoute.fullPath}
                  />
                ),
              },
            )
          }
        </Text>
      ) : null}
      <Text styleAs="bodySmall" mt="1em">
        <Text styleAs="h5" tag="span">
          {
            /* TRANSLATORS: Salary survey footer */
            i18n('We take data security very seriously. ')
          }
        </Text>
        {
          /* TRANSLATORS: Salary survey footer */
          i18n(
            `{{link}}Check out our FAQ{{/link}} to learn about how we protect your privacy.`,
            {
              link: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={salariesFaqRoute.fullPath}
                />
              ),
            },
          )
        }
      </Text>
    </>
  ) : (
    <div>
      {isStepZero ? (
        <Text styleAs="bodySmall">
          {
            /* TRANSLATORS: Salary survey footer */
            i18n(
              `Want to learn more about Idealist's Nonprofit Salary Explorer? {{link}}Click here to learn more.{{/link}}`,
              {
                link: (
                  <StyledNativeLinkWithRef
                    target="_blank"
                    href={salariesFaqRoute.fullPath}
                  />
                ),
              },
            )
          }
        </Text>
      ) : null}

      <Text styleAs="bodySmall" mt="1em">
        <Text styleAs="h5" tag="span">
          {
            /* TRANSLATORS: Salary survey footer */
            i18n('We take data security very seriously. ')
          }
        </Text>
        <span>
          {i18n(`The information you enter in the survey will not be linked to your
          user account, and will be completely anonymized. `)}
        </span>
        {
          /* TRANSLATORS: Salary survey footer */
          i18n('Want to know more? ')
        }
        {
          /* TRANSLATORS: Salary survey footer */
          i18n(
            `{{link}}Check out our FAQ{{/link}} to learn about how we protect your privacy.`,
            {
              link: (
                <StyledNativeLinkWithRef
                  target="_blank"
                  href={salariesFaqRoute.fullPath}
                />
              ),
            },
          )
        }
      </Text>
    </div>
  );
}

function Loading() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      minHeight={300}
    >
      <LoadingDots />
    </Box>
  );
}

const getSteps = ({
  i18n,
  alreadySubmitted,
  user,
  isUserLoaded,
  titleChanged,
  surveyApiKey,
}: {
  i18n: I18nFn;
  alreadySubmitted: boolean;
  user: ApiUser | null;
  isUserLoaded: boolean;
  titleChanged: boolean;
  surveyApiKey: string;
}) => {
  const userName = user ? (user.firstName ?? user.name) : null;
  let title;

  if (userName) {
    if (alreadySubmitted) {
      title = i18n('Welcome back, %(userName)s!', { userName });
    } else {
      title = i18n('Welcome, %(userName)s!', { userName });
    }
  } else if (alreadySubmitted) {
    title = i18n('Welcome back!');
  } else {
    title = i18n('Welcome!');
  }

  const stepZero = {
    hideNextStep: alreadySubmitted && titleChanged === false,
    submitIsDisabled: alreadySubmitted && titleChanged === null,
    loadingSection: !isUserLoaded || !surveyApiKey ? <Loading /> : null,
    title,
    eventName: 'Salary Survey Start Screen',
    eventProperties:
      user && alreadySubmitted
        ? {
            already_submitted: alreadySubmitted,
          }
        : undefined,
    nextText: alreadySubmitted === false ? i18n(`That's me! Let's go!`) : null,
    leftButtons: (() => {
      if (alreadySubmitted) {
        if (titleChanged === false) {
          return (
            <Button
              type="link"
              variant="primary"
              size="large"
              qaId="view-salary-report"
              href={salariesExploreRoute.fullPath}
            >
              {i18n("Let's Go!")}

              <Box display="inline-block" ml={12}>
                <Icon name="arrow-right" size={18} />
              </Box>
            </Button>
          );
        }
        return null;
      }

      return (
        <Button
          type="link"
          variant="secondary"
          href={salariesLearnMoreRoute.fullPath}
          qaId="learn-more-button"
          size="large"
        >
          {i18n("That's Not Me")}
        </Button>
      );
    })(),
    sections: [
      {
        title,
        intro: (() => {
          if (alreadySubmitted === null) {
            return <Loading />;
          }

          if (alreadySubmitted === false) {
            return (
              <Body>
                {i18n(
                  "We're currently accepting survey data from people who have worked {{u}}full time (35+ hours/week) at a nonprofit{{/u}} in the U.S. within the last three years.",
                  { u: <Underline /> },
                )}
              </Body>
            );
          }

          return (
            <Body>
              {i18n(
                'Have there been any significant changes since you last told us about your job?',
              )}
            </Body>
          );
        })(),
        rows: alreadySubmitted
          ? [
              {
                fields: ['titleChanged'],
              },
            ]
          : [],
      },
    ],
    ignoreInNav: true,
    footer: <Footer loggedOut={!user} isStepZero />,
  };
  const otherSteps = [
    {
      title: i18n('You and Your Job'),
      eventName: 'Your Job',
      nextText: i18n('Continue'),
      sections: [
        {
          title: i18n('You and Your Job'),
          intro: (
            <>
              <BodySmall>
                {i18n(
                  'Tell us about you! Please provide the required information (marked with an *) below.',
                )}
              </BodySmall>

              <BodySmall mt="8px">
                {
                  /* TRANSLATORS: Salary survey footer */
                  i18n(
                    `A reminder: your information will be anonymized and kept completely confidential. Check out our {{link}}FAQ{{/link}} to learn more.`,
                    {
                      link: (
                        <StyledNativeLinkWithRef
                          target="_blank"
                          href={salariesFaqRoute.fullPath}
                        />
                      ),
                    },
                  )
                }
              </BodySmall>
            </>
          ),
          rows: [
            {
              fields: ['jobTitle'],
              fieldsToValidate: ['selectedSynonymId', 'titleSuggestion'],
            },
            {
              fields: ['annualSalary'],
            },
            {
              fields: ['managerialStatus'],
            },
            {
              fields: ['yearsOfExperience'],
            },
            {
              fields: ['educationLevel'],
            },
          ],
        },
      ],
      footer: <Footer />,
    },
    {
      title: i18n('Your Employer'),
      eventName: 'Your Employer',
      nextText: i18n('Continue'),
      sections: [
        {
          title: i18n('Your Employer'),
          rows: [
            {
              fields: ['location'],
              fieldsToValidate: [
                'remoteStatus',
                'salaryBasedOn',
                'workCity',
                'workStateCode',
                'workLatitude',
                'workLongitude',
                'employerCity',
                'employerStateCode',
                'employerLatitude',
                'employerLongitude',
              ],
            },
            {
              fields: ['employerOrgType'],
            },
            {
              fields: ['employerOrgSubType'],
            },
            {
              fields: ['employerOrgDescriptor'],
              fieldsToValidate: [],
            },
            {
              fields: ['employerNEmployeesBucket', null],
            },
          ],
        },
      ],
      footer: <Footer />,
    },
    {
      title: i18n('Equity and Inclusion'),
      eventName: 'Equity & Inclusion',
      nextText: i18n('Submit'),
      sections: [
        {
          title: i18n('Equity and Inclusion'),
          intro: (
            <>
              <BodySmall mb={15}>
                {i18n(
                  'While the optional questions below are personal in nature, rest assured that Idealist will anonymize your answers and protect your data. Visit our {{link}}FAQ{{/link}} to learn more.',
                  {
                    link: (
                      <StyledNativeLinkWithRef
                        target="_blank"
                        href={salariesFaqRoute.fullPath}
                      />
                    ),
                  },
                )}
              </BodySmall>
              <BodySmall>
                {i18n(`With your participation, you have the potential to
                impact and influence pay equity in our sector.`)}
              </BodySmall>
            </>
          ),
          rows: [
            {
              fields: ['gender'],
            },
            {
              fields: ['genderFreeText'],
            },
            {
              fields: ['race'],
            },
            {
              fields: ['raceFreeText'],
            },
            {
              fields: ['ageBucket', null],
            },
            {
              fields: ['disabilityStatus'],
            },
            {
              fields: ['militaryServiceStatus'],
            },
          ],
        },
      ],
      footer: <Footer />,
    },
  ];
  // @ts-expect-error TS(2769): No overload matches this call.
  return [stepZero].concat(otherSteps);
};

const getValuesToValidate = (
  values: Record<string, unknown>,
  validate: string[],
) =>
  Object.fromEntries(
    Object.entries(values).filter(([k, _v]) =>
      [...validate, 'publish'].includes(k),
    ),
  );

function Success() {
  const { i18n } = useI18n();

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={96}
      textAlign="center"
    >
      <SuccessCheckmark containerSize={64} iconSize={32} />

      <H4 mb={64}>{i18n('Thanks for your input!')}</H4>

      <LoadingDots />

      <BodySmall mt={24}>{i18n('Securely submitting your data')}</BodySmall>
    </Box>
  );
}

export function SalarySurveyPage() {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { environment } = useEnvironment();

  const { navigateToStep } = useMultiStep();

  const [surveyApiKey, setSurveyApiKey] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const oneMonthAgo = subMonths(new Date(), 1);
  const submissions = getItem('salarySurveyLastSubmitted');
  // oxlint-disable-next-line typescript/no-explicit-any
  let submittedTooManyTimes: any;

  if (submissions && Array.isArray(submissions)) {
    submittedTooManyTimes =
      submissions.filter((dateStr) => parseJSON(dateStr) > oneMonthAgo)
        .length >= 5;
  }

  const lastSubmitted = salarySurveyLastSubmittedDate(user);

  useEffect(() => {
    void salaryApiGetCompensationSurvey().then(
      ({ alreadySubmitted: newAlreadySubmitted }) => {
        if (submittedTooManyTimes) {
          navigationFlash(
            'We already have your survey information, but we love your enthusiasm!',
          );
          void navigate(salariesExploreRoute.with({}));
        } else {
          setAlreadySubmitted(newAlreadySubmitted);
        }
        setAlreadySubmitted(Boolean(getItem('salarySurveyLastSubmitted')));

        return undefined;
      },
    );
  }, [navigate, submittedTooManyTimes]);

  useEffect(() => {
    void apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
      // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      setSurveyApiKey(newApiKey);

      return undefined;
    });
  }, []);

  const initialValues = useMemo(
    () => ({
      titleSuggestion: '',
      selectedSynonymId: null,
      managerialStatus: false,
      yearsOfExperience: null,
      educationLevel: '',
      ageBucket: '',
      employerBudgetBucket: '',
      employerOrgType: '',
      employerNEmployeesBucket: '',
      remoteStatus: '',
      salaryBasedOn: '',
      workCity: '',
      workStateCode: '',
      workGeoType: '',
      employerCity: '',
      employerStateCode: '',
      annualSalary: '',
      disabilityStatus: null,
      militaryServiceStatus: null,
      publish: false,
      validate: [],
      titleChanged: null,
      // does not submit to server
      // TODO: Remove when testing is done.
      source: getParam(location, 'test') ? 'TEST' : 'MAIN_SURVEY',
    }),
    [location],
  );

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const CompensationSurveyForm = ({
    values,
    updateValueWith,
  }: FormSubmitFunctionData<{
    // TODO: add complete types
    titleChanged: boolean;
  }>) => {
    if (!surveyApiKey) {
      return <Loading />;
    }

    const fields = getCompensationSurveyFields(
      i18n,
      locale,
      // @ts-expect-error TS(2345): Argument of type '(arg0: string | string[], arg1: ... Remove this comment to see the full error message
      updateValueWith,
      lastSubmitted,
      surveyApiKey,
    );
    const steps = getSteps({
      i18n,
      alreadySubmitted,
      user,
      isUserLoaded: Boolean(environment),
      titleChanged: values.titleChanged,
      surveyApiKey,
    });

    return (
      <MultiStepForm
        // @ts-expect-error TS(2322): Type '{ hideNextStep: any; submitIsDisabled: any; ... Remove this comment to see the full error message
        steps={steps}
        fields={fields}
        hasTracking
        logo={
          <StyledNativeLinkWithRef href="/">
            <IdealistLogo withText height={52} />
          </StyledNativeLinkWithRef>
        }
        successSection={submitted ? <Success /> : null}
        sidebar={[
          {
            imageUrl: getStaticImageSrc(
              'compdb/survey-step1.svg',
              appConfig.idealist.staticImgPath,
            ),
            width: 270,
            body: (
              <BodyMicro px="48px">
                {i18n(
                  'After answering a few questions, you’ll gain access to our salary calculator, a powerful tool to support you as you try to negotiate pay, switch roles, or relocate to a new city.',
                )}
              </BodyMicro>
            ),
          },
          {
            imageUrl: getStaticImageSrc(
              'compdb/survey-step2.svg',
              appConfig.idealist.staticImgPath,
            ),
            width: 320,
          },
          {
            imageUrl: getStaticImageSrc(
              'compdb/survey-step0.svg',
              appConfig.idealist.staticImgPath,
            ),
            width: 270,
          },
          {
            imageUrl: getStaticImageSrc(
              'compdb/survey-step3.svg',
              appConfig.idealist.staticImgPath,
            ),
            width: 310,
          },
        ]}
      />
    );
  };

  const addSubmissionToLocalStorage = () => {
    const newSubmissionDate = now({
      dtOverride: appConfig.idealist.dtOverride,
    });

    if (submissions && Array.isArray(submissions)) {
      setItem('salarySurveyLastSubmitted', [...submissions, newSubmissionDate]);
    } else {
      setItem('salarySurveyLastSubmitted', [newSubmissionDate]);
    }
  };

  const onSubmit: FormSubmitFunction<
    SalariesCompensationSurveyData & MultiStepFormValues
  > = ({ values, handleError, updateSubmitting }) => {
    const source: SalariesCompensationSurveyData['source'] = getParam(
      location,
      'test',
    )
      ? 'TEST'
      : 'MAIN_SURVEY';
    const valuesWithSource = {
      ...values,
      source,
    };
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line typescript/no-explicit-any
    const valuesToValidate: Record<string, any> = valuesWithSource.validate
      ? getValuesToValidate(valuesWithSource, valuesWithSource.validate)
      : [];

    if (valuesWithSource.hasNextStep) {
      return salaryApiValidateCompensationSurvey(
        valuesToValidate,
        source === 'TEST',
      )
        .then(() => {
          navigateToStep((prevStep) => prevStep + 1);
          updateSubmitting(false);

          return undefined;
        })
        .catch(handleError);
    }

    return salaryApiSubmitCompensationSurvey(valuesWithSource)
      .then(() => {
        trackEvent('Success: Submitted Survey');
        addSubmissionToLocalStorage();
        setSubmitted(true);

        // only needed for google/facebook, but save anyway
        return apiCreateTmpStorage({
          type: 'survey-report',
          surveyReport: {
            jobTitleId: values.jobTitleId,
            jobTitle: values.jobTitle,
            selectedSynonymId: values.selectedSynonymId,
            selectedSynonymFamilyId: values.selectedSynonymFamilyId,
            city: values.workCity ?? undefined,
            stateCode: values.workStateCode ?? undefined,
            cityStateUnified:
              values.workCity && values.workStateCode
                ? `${values.workCity}, ${values.workStateCode}`
                : undefined,
            latitude: values.workLatitude,
            longitude: values.workLongitude,
            yearsOfExperience: values.yearsOfExperience,
            geoType: values.workGeoType,
          },
        } satisfies SalaryApiTmpStorage).then((tmpId) => {
          window.location.href = salariesExploreRoute.with({
            exploreTmpId: tmpId,
          });

          return undefined;
        });
      })
      .catch(handleError);
  };

  return (
    <Form
      analyticsTitle="[salaries] Compensation Survey"
      usageContext="page"
      qaId="compensation-survey-form"
      // @ts-expect-error fix types
      initialValues={initialValues}
      onSubmit={onSubmit}
      // @ts-expect-error fix types
      render={CompensationSurveyForm}
    />
  );
}
